import { cn } from '@components/shadcn/utils';
import dayjs from '@utils/dateUtils';
import { Badge } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationCenter } from '../NotificationCenter.models';

interface BaseNotificationProps {
  notification: NotificationCenter.Notification;
  icon: React.ReactNode;
  isSystemNotification?: boolean;
  /** Overrides the title derived from the service/trigger — for payload-driven phrase keys. */
  title?: string;
  ActionComponent?: React.FC<{ readonly notification: NotificationCenter.Notification }>;
}

function BaseNotification({
  notification,
  icon,
  isSystemNotification,
  title,
  ActionComponent,
}: Readonly<BaseNotificationProps>) {
  const { t } = useTranslation();

  const resolvedTitle =
    title ??
    (isSystemNotification
      ? t(`notifications.${notification.service}.${notification.trigger}.title`)
      : t(`notifications.${notification.service}.title`));

  return (
    <div
      className={cn('px-5 py-2 rounded-md bg-white', {
        ['bg-sy-blue-10']: notification.status === 'UNREAD',
      })}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">{icon}</div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-base line-clamp-2 min-w-0" title={resolvedTitle}>
              {resolvedTitle}
            </span>
            {notification.status === 'UNREAD' && <Badge className="flex-shrink-0" color="red" />}
          </div>
          {ActionComponent ? (
            <ActionComponent notification={notification} />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
              {t(`notifications.${notification.service}.${notification.trigger}`)}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">{dayjs(notification.date).calendar()}</p>
        </div>
      </div>
    </div>
  );
}

export { BaseNotification };
